import React, { useCallback } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ClickableContainer } from 'components/clickable-container';
// TODO Move the section wrapper to the home-page.view.ts so that it can be reused
import { Section } from 'components/features/components/marketing-tile-group/styles';
import { SecondaryLink } from 'components/link';
import Picture from 'components/picture';
import SanityBlockRenderer, { SanityBlockContentLinkProps } from 'components/sanity-block-renderer';
import { useGetAllRollUpTheRimsQuery } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames } from 'state/mParticle';

import { useLocalizedUrls } from '../hooks/use-localized-urls';
import { useRollsRemaining } from '../hooks/use-rolls-remaining';
import { GameplayButton } from '../sections/gameplay/components';
import { GameplayBackgroundGradient } from '../sections/gameplay/components/gameplay-background/gameplay-background';
import { TypographyContainer, TypographyContainerProps } from '../typography/sanity-block-content';

import { IHomepageBannerViewProps } from './types';

const BannerWrapper = styled(Section)``;

const Background = styled(GameplayBackgroundGradient)`
  border-radius: ${Styles.borderRadius};
`;

/* override some styles from SanityBlockContent specific to this banner*/
const HomepageBannerTypography = styled(TypographyContainer)<TypographyContainerProps>`
  h2 {
    letter-spacing: -1.9px;
    font-size: 2.75rem;
    font-weight: 900;
    font-style: normal;
  }
  h3 {
    letter-spacing: -1.9px;
    font-size: 2.1rem;
    font-weight: 900;
    font-style: normal;
  }
  ${Styles.desktop`
    h2 {
      font-size: 3.7rem;
    }
    h3 {
      font-size: 3.7rem;
    }
    padding-bottom: 1.5rem;
  `}
`;

const TopContent = styled(ClickableContainer)`
  display: flex;
  justify-content: center;
  cursor: pointer;
  flex: 1;
`;

const ClickWrap = styled(ClickableContainer)`
  display: flex;
  justify-content: center;
  cursor: pointer;
  flex: 1;
`;

const MobileLeft = styled.div`
  display: flex;
  flex: 4;
  padding-left: 1rem;
  padding-top: 2rem;
`;

const MobileRight = styled.div`
  display: flex;
  flex: 6;
  max-height: 300px;
`;

const DesktopLeft = styled.div`
  display: flex;
  flex: 6;
  padding-top: 2rem;
`;

const DesktopRight = styled.div`
  display: flex;
  flex: 3;
  max-height: 300px;
`;

const Image = styled(Picture)`
  display: flex;
`;

const MobileButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const DesktopButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-start;
`;

const ButtonWrapper = styled.div`
  display: block;
  padding: 0.4rem 0;
  ${Styles.desktop`
    padding: 0;
  `}
`;

const GameplayButtonWrapper = styled(GameplayButton)`
  padding: 0.1rem;
  div > div {
    padding: 0 0.4rem;
  }
`;

const GameplayButtonText = styled.div`
  font-size: 0.8rem;
`;

const RulesText = styled.p`
  color: ${Styles.color.white};
  font-size: 0.75em;
  line-height: 1;
  margin: 0;
  padding: 0 1rem 1rem;
`;

const StyledLink = styled(SecondaryLink)`
  font-size: 0.75em;
  font-weight: ${Styles.fontWeight.heavy};
  margin-top: 0.5rem;
`;

const LinkText = styled.span`
  text-decoration: underline;
`;

const LinkTriangle = styled.span`
  font-size: 0.75em;
`;

const rulesTextSerializers = {
  marks: {
    link: ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
      const [linkText] = children;
      const { href } = mark;
      return href && linkText ? (
        <StyledLink to={href}>
          <LinkText>{linkText}</LinkText> <LinkTriangle>&#9654;</LinkTriangle>
        </StyledLink>
      ) : null;
    },
  },
};

export const HomepageBannerView: React.FC<IHomepageBannerViewProps> = ({
  cupImage,
  enterButtonLabel,
  enterButtonUrl,
  header,
  rulesText,
  isAuthenticated,
  rollsRemaining,
  rollsRemainingLoading,
}) => {
  const navigate = useNavigate();
  const onClick = useCallback(() => {
    navigate(enterButtonUrl);
  }, [enterButtonUrl, navigate]);

  const isMobile = useMediaQuery('mobile');

  const buttons = (
    <ButtonWrapper>
      {!isAuthenticated ? (
        <GameplayButtonWrapper
          onClick={onClick}
          isRollButton
          to={enterButtonUrl}
          hasRollCount={false}
          eventName={CustomEventNames.RUTR_BUTTON_CLICK_HOME_PAGE_TIME_TO_PLAY}
        >
          {enterButtonLabel}
        </GameplayButtonWrapper>
      ) : (
        <GameplayButtonWrapper
          rollsRemaining={rollsRemaining}
          onClick={onClick}
          isRollButton
          eventName={CustomEventNames.RUTR_BUTTON_CLICK_HOME_PAGE_TIME_TO_PLAY}
          isLoading={rollsRemainingLoading}
          isSmallBtn
        >
          <GameplayButtonText>{enterButtonLabel}</GameplayButtonText>
        </GameplayButtonWrapper>
      )}
    </ButtonWrapper>
  );
  const imageEl = cupImage && <Image image={cupImage} alt="" />;
  const termsText = rulesText && (
    <SanityBlockRenderer
      blockContentOverrides={{
        normal: ({ children }) => <RulesText>{children}</RulesText>,
      }}
      content={rulesText}
      serializers={rulesTextSerializers}
    />
  );

  // Braking this up since the content is structured differently
  let content;
  if (isMobile) {
    content = (
      <Box justify="center" flexDirection="column" height="100%">
        <TopContent onClick={onClick}>
          <MobileLeft>
            <Box flexDirection="column">
              <HomepageBannerTypography centered={false} inverted content={header} />
              <MobileButtonContainer>{buttons}</MobileButtonContainer>
            </Box>
          </MobileLeft>
          <MobileRight>{imageEl}</MobileRight>
        </TopContent>
        {termsText}
      </Box>
    );
    // Desktop view
  } else {
    content = (
      <Box justify="center">
        <Box maxWidth="40rem">
          <DesktopLeft>
            <Box flexDirection="column">
              <ClickableContainer onClick={onClick}>
                <HomepageBannerTypography centered={false} inverted content={header} />
              </ClickableContainer>
              <DesktopButtonContainer>
                {buttons}
                {termsText}
              </DesktopButtonContainer>
            </Box>
          </DesktopLeft>
          <DesktopRight>
            <ClickWrap onClick={onClick}>{imageEl}</ClickWrap>
          </DesktopRight>
        </Box>
      </Box>
    );
  }

  return (
    <BannerWrapper data-testid="HomepageBannerView">
      <Background>{content}</Background>
    </BannerWrapper>
  );
};

const HomepageBanner: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  const isUserAuthenticated = isAuthenticated();
  const { rollUpTheRimLandingPageUrl, rollUpTheRimRulesUrl } = useLocalizedUrls();
  const { data: sanityData, loading: rutrLoading } = useGetAllRollUpTheRimsQuery();
  const { rollsRemaining, loading: rollsRemainingLoading } = useRollsRemaining({
    isAuthenticated: isUserAuthenticated,
  });
  const { formatMessage } = useIntl();
  const enableRutr = useFlag(LaunchDarklyFlag.ENABLE_RUTR);
  const enableRutrHomePageBanner = useFlag(LaunchDarklyFlag.ENABLE_RUTR_HOME_PAGE_BANNER);

  // Always hide if the `enableRutrHomePageBanner` flag is off
  if (!enableRutrHomePageBanner) {
    return null;
  }

  const enterButtonUrl = () => {
    // If the RUTR campaign is still running
    // We have a separate LD flag to enable this component
    if (enableRutr) {
      return isUserAuthenticated
        ? rollUpTheRimLandingPageUrl
        : formatMessage({ id: 'routes.signIn' });
    }
    // If not redirect to the RUTR rules
    return rollUpTheRimRulesUrl;
  };

  if (rutrLoading || !sanityData) {
    return null;
  }

  const rollUpTheRim = sanityData?.allRollUpTheRims?.[0];
  const enterButtonLabel = rollUpTheRim?.enterButtonText?.localeRaw?.[0]?.children?.[0]?.text || '';
  const header = rollUpTheRim?.homepageHeader?.localeRaw || [];
  const cupImage = rollUpTheRim?.homepageCupImage?.locale?.app || null;
  const rulesText = rollUpTheRim?.rulesText?.localeRaw || [];

  return (
    <HomepageBannerView
      cupImage={cupImage}
      enterButtonLabel={enterButtonLabel}
      enterButtonUrl={enterButtonUrl()}
      header={header}
      rulesText={rulesText}
      isAuthenticated={isUserAuthenticated}
      rollsRemaining={rollsRemaining}
      rollsRemainingLoading={rollsRemainingLoading}
    />
  );
};

export default HomepageBanner;
